import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../../../redux/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faShieldAlt, faMobileAlt } from '@fortawesome/free-solid-svg-icons';
import { useOTPState } from './hooks';
import { verify2FA, resendOTP } from '../store';
import { RESEND_COOLDOWN } from '../constants';
import { OTPInputProps } from './types';
import {
  AuthContainer,
  ButtonWrapper,
  PrimaryButton,
  LoadingIcon,
  HelpText,
  ModernFormCard,
  ModernTitle,
  IconWrapper,
  IconCircle,
  SectionSpacer,
  ModernFormContainer,
  FormItem,
  MaskedContactDisplay,
  MaskedContactLabel,
  MaskedContactValue,
} from '../styles';
import { ThemeProvider } from 'styled-components';
import OTPInputFields from './OTPInputFields';
import PhoneDisplay from './PhoneDisplay';
import ErrorView from './ErrorView';

const OTPInput: React.FC<OTPInputProps> = ({ setIsLoggedIn, phone, emailOrUsername, twoFactorMethod, maskedContact }) => {
  const { isDarkMode } = useSelector((state: RootState) => state.theme);
  const { twoFactorMethod: reduxTwoFactorMethod, maskedContact: reduxMaskedContact } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isVerifyingOTP, isResendingOTP } = useSelector((state: RootState) => state.auth);

  const activeTwoFactorMethod = twoFactorMethod || reduxTwoFactorMethod;
  const activeMaskedContact = maskedContact || reduxMaskedContact;
  
  const {
    otp,
    setOtp,
    isResendDisabled,
    setIsResendDisabled,
    resendTimer,
    setResendTimer,
    otpRequestCount,
    setOtpRequestCount,
  } = useOTPState();

  const handleOtpChange = useCallback((index: number, value: string): void => {
    if (value.length > 1 || !/^\d*$/.test(value)) return;
    
    setOtp((prev) => {
      const newOtp = [...prev];
      newOtp[index] = value;
      return newOtp;
    });

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`) as HTMLInputElement;
      nextInput?.focus();
    }
  }, [setOtp]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`) as HTMLInputElement;
      prevInput?.focus();
    }
  }, [otp]);

  const onVerify = useCallback(async (): Promise<void> => {
    if (otp.join('').length !== 6 || isVerifyingOTP) return;

    const code = otp.join('');
    const result = await dispatch(verify2FA({ emailOrUsername, code, navigate }));

    if (verify2FA.fulfilled.match(result)) {
      setIsLoggedIn(true);
      setOtpRequestCount(0);
      localStorage.removeItem('otpRequestCount');
    }
  }, [emailOrUsername, otp, setIsLoggedIn, navigate, dispatch, isVerifyingOTP, setOtpRequestCount]);

  const onResendOtp = useCallback(async (): Promise<void> => {
    if (isResendDisabled || isResendingOTP) return;

    const result = await dispatch(resendOTP({ emailOrUsername, phone, otpRequestCount }));

    if (resendOTP.fulfilled.match(result)) {
      setOtpRequestCount((prev) => {
        const newCount = prev + 1;
        localStorage.setItem('otpRequestCount', newCount.toString());
        return newCount;
      });
      setResendTimer(RESEND_COOLDOWN);
      setIsResendDisabled(true);
      localStorage.setItem('resendCooldown', Date.now().toString());
    }
  }, [emailOrUsername, phone, otpRequestCount, setOtpRequestCount, setResendTimer, setIsResendDisabled, isResendDisabled, isResendingOTP, dispatch]);

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'dark' | 'light' };

  if ((!phone || phone.trim() === '') && (!activeTwoFactorMethod || !activeMaskedContact)) {
    return (
      <ThemeProvider theme={theme}>
        <AuthContainer>
          <ModernFormContainer>
            <ModernFormCard>
              <ErrorView />
            </ModernFormCard>
          </ModernFormContainer>
        </AuthContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <AuthContainer>
        <ModernFormContainer>
          <ModernFormCard>
            <IconWrapper>
              <IconCircle>
                <FontAwesomeIcon icon={faShieldAlt} />
              </IconCircle>
              <IconCircle>
                <FontAwesomeIcon icon={faMobileAlt} />
              </IconCircle>
            </IconWrapper>

            <ModernTitle>
              {activeTwoFactorMethod ? 'Verify Your Identity' : 'Verify Your Phone Number'}
            </ModernTitle>

            {phone && <PhoneDisplay phone={phone} />}
            {activeTwoFactorMethod && activeMaskedContact && (
              <FormItem>
                <MaskedContactDisplay>
                  <MaskedContactLabel>We've sent a code to:</MaskedContactLabel>
                  <MaskedContactValue>{activeMaskedContact}</MaskedContactValue>
                </MaskedContactDisplay>
              </FormItem>
            )}

            <SectionSpacer />

            <OTPInputFields
              otp={otp}
              onOtpChange={handleOtpChange}
              onKeyDown={handleKeyDown}
              onResendOtp={onResendOtp}
              isResendDisabled={isResendDisabled}
              resendTimer={resendTimer}
              isResending={isResendingOTP}
            />

            <ButtonWrapper>
              <PrimaryButton
                type="primary"
                onClick={onVerify}
                block
                disabled={otp.join('').length !== 6 || isVerifyingOTP}
                icon={isVerifyingOTP ? <LoadingIcon icon={faSpinner} /> : undefined}
              >
                {isVerifyingOTP ? 'Verifying...' : 'Verify'}
              </PrimaryButton>
            </ButtonWrapper>

            <HelpText>
              Didn't receive the code? Check your spam folder or try resending.
            </HelpText>
          </ModernFormCard>
        </ModernFormContainer>
      </AuthContainer>
    </ThemeProvider>
  );
};

export default OTPInput;
